<template>
  <div class="c-events-list">
    <div class="c-events-list__wrapper">
      <div class="c-events-list__header">
        <h1 class="c-events-list__heading">
          {{ translations.EventsListHeading }}
        </h1>
        <div class="c-events-list__filters">
          <div
            class="c-events-list__filter"
            v-for="filter in filters"
            :key="filter.id"
          >
            <label :for="filter.id">
              <span>{{ filter.label }}</span>
              <input
                type="checkbox"
                :id="filter.id"
                v-model="filter.checked"
                @change="handleFilterChange"
              />
            </label>
          </div>
        </div>
      </div>
      <div aria-live="polite">
        <div
          v-if="loading"
          class="c-events-list__loading"
          style="padding: 3rem 0 0; text-align: center"
        >
          {{ translations.LoadingText || "Loading..." }}
        </div>

        <ul v-else class="c-events-list__events">
          <li
            v-if="displayedEvents.length === 0"
            class="c-events-list__event--no-results c-dashboard__card__no-events"
          >
            <h3>{{ translations.NoEventsText || "No events found." }}</h3>
          </li>
          <li
            v-else
            v-for="event in displayedEvents"
            :key="event.id"
            class="c-events-list__event"
          >
            <div class="c-events-list__event__info">
              <h2 v-html="event.name"></h2>
              <p>{{ formatDate(event.event_date) }}</p>
            </div>
            <a
              :href="event.greeting_url"
              class="c-events-list__event__cta"
              target="_blank"
              rel="noopener noreferrer"
              >More Details</a
            >
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "EventsList",

  props: {
    translations: {
      type: Object,
      required: true,
      default: () => ({
        EventsListHeading: "Heart Challenge Events",
        LoadingText: "Loading...",
        NoEventsText: "No events found.",
        MoreDetailsText: "More Details",
      }),
    },
    apiConfig: {
      type: Object,
      default: () => ({
        apiKey: "wDB09SQODRpVIOvX",
        baseUrl: "https://www2.heart.org/site/CRTeamraiserAPI",
      }),
    },
    customFilters: {
      type: Array,
      default: () => ["Heart Walk", "Field Day", "Cyclenation"],
    },
    dateLocale: {
      type: String,
      default: "en-US",
    },
  },

  data() {
    return {
      allEvents: [],
      loading: true,
      filters: this.transformFilters(this.customFilters),
    };
  },

  methods: {
    transformFilters(filterLabels) {
      return filterLabels.map((label) => ({
        id: label.toLowerCase().replace(/\s+/g, "-"),
        label: label,
        checked: true, // Set to true by default
      }));
    },

    async fetchEvents() {
      const searchParams = this.getUrlParams();
      let apiParams = {
        v: "1.0",
        api_key: this.apiConfig.apiKey,
        response_format: "json",
        list_page_size: 500,
        list_sort_column: "event_date",
        list_ascending: "true",
      };

      if (searchParams.state) {
        apiParams = {
          ...apiParams,
          method: "getTeamraisersByInfo",
          state: searchParams.state,
        };
      } else if (searchParams.zipcode && searchParams.distance) {
        apiParams = {
          ...apiParams,
          method: "getTeamraisersByDistance",
          starting_postal: searchParams.zipcode,
          search_distance: searchParams.distance,
        };
      } else {
        apiParams = {
          ...apiParams,
          method: "getTeamraisersByInfo",
        };
      }

      try {
        const queryString = new URLSearchParams(apiParams).toString();
        const response = await fetch(
          `${this.apiConfig.baseUrl}?${queryString}`
        );
        const data = await response.json();

        if (
          data.getTeamraisersResponse &&
          data.getTeamraisersResponse.teamraiser
        ) {
          this.allEvents = Array.isArray(data.getTeamraisersResponse.teamraiser)
            ? data.getTeamraisersResponse.teamraiser
            : [data.getTeamraisersResponse.teamraiser];
        } else {
          this.allEvents = [];
        }
      } catch (error) {
        console.error("API call failed:", error);
        this.allEvents = [];
      } finally {
        this.loading = false;
      }
    },

    getUrlParams() {
      const params = new URLSearchParams(window.location.search);
      const searchParams = {};

      if (params.has("state")) {
        searchParams.state = params.get("state");
      } else if (params.has("zipcode") && params.has("distance")) {
        searchParams.zipcode = params.get("zipcode");
        searchParams.distance = params.get("distance");
      }

      return searchParams;
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString(this.dateLocale, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    },

    handleFilterChange() {
      // Filter changes are handled through the displayedEvents computed property
    },
  },

  computed: {
    displayedEvents() {
      const uncheckedFilters = this.filters
        .filter((f) => !f.checked)
        .map((f) => f.id.replace("-", " ").toLowerCase());

      if (uncheckedFilters.length === 0) {
        return this.allEvents;
      }

      return this.allEvents.filter((event) => {
        const eventName = event.name.toLowerCase();
        // Return true if the event name doesn't match any unchecked filters
        return !uncheckedFilters.some((filter) => eventName.includes(filter));
      });
    },
  },

  created() {
    this.fetchEvents();
  },
};
</script>
<style>
.c-events-list {
  padding-block-end: 6rem;
}
</style>



// WEBPACK FOOTER //
// src/app/components/wbwb/wbwb-event-list.vue